import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { Box, Button, FormControl, MenuItem, Select, TextField, Typography } from '@mui/material';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Technician } from '../../../types/technician-types';
import { DateUtils } from '../../../utils';
import {
  buildInactiveTechnicianLookup,
  getTechnicianDisplayName,
} from '../technician-display-utils';
import { ProductionStage } from '../types';

interface ProductionTrackerProps {
  stages: ProductionStage[];
  technicians: Technician[];
  isLocked: boolean;
  canRecordStages: boolean;
  canAdminOverride: boolean;
  onSaveStage: (
    stageId: number,
    data: { technicianId: string; date: string; initials: string }
  ) => Promise<void>;
  onDirtyChange?: (isDirty: boolean) => void;
}

interface StageRowState {
  technicianId: string;
  date: string;
  initials: string;
  saving: boolean;
  justSaved: boolean;
}

function getLocalDateString(date = new Date()): string {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

const ProductionTracker = ({
  stages,
  technicians,
  isLocked,
  canRecordStages,
  canAdminOverride,
  onSaveStage,
  onDirtyChange,
}: ProductionTrackerProps) => {
  const [stageInputs, setStageInputs] = useState<Record<number, StageRowState>>({});
  const inactiveTechnicianLookup = useMemo(
    () => buildInactiveTechnicianLookup(stages, technicians),
    [stages, technicians]
  );

  useEffect(() => {
    if (!onDirtyChange) return;
    const hasDirtyInputs = Object.values(stageInputs).some(
      input => input.technicianId && !input.justSaved && !input.saving
    );
    onDirtyChange(hasDirtyInputs);
  }, [stageInputs, onDirtyChange]);

  const getStageInput = useCallback(
    (stageId: number): StageRowState => {
      if (stageInputs[stageId]) return stageInputs[stageId];

      const stage = stages.find(s => s.id === stageId);
      const hasExistingData =
        stage && (stage.status === 'complete' || stage.status === 'locked') && stage.technicianId;
      const isAdminEdit = canAdminOverride && isLocked && hasExistingData;

      return {
        technicianId: hasExistingData ? stage.technicianId! : '',
        date: hasExistingData && stage.date ? stage.date.substring(0, 10) : getLocalDateString(),
        initials: hasExistingData && stage.initials ? stage.initials : '',
        saving: false,
        justSaved: !!isAdminEdit,
      };
    },
    [stageInputs, stages, canAdminOverride, isLocked]
  );

  const updateStageInput = (stageId: number, field: string, value: string) => {
    const current = getStageInput(stageId);
    const updates: Partial<StageRowState> = { justSaved: false };

    if (field === 'initials') {
      updates.initials = value.toUpperCase().slice(0, 3);
    } else if (field === 'technicianId') {
      updates.technicianId = value;
      const tech = technicians.find(t => String(t.id) === String(value));
      if (tech) {
        updates.initials = tech.initials.toUpperCase().slice(0, 3);
      }
    } else {
      (updates as Record<string, string>)[field] = value;
    }

    setStageInputs(prev => ({
      ...prev,
      [stageId]: { ...current, ...updates },
    }));
  };

  const getDateConstraints = useCallback(
    (stage: ProductionStage) => {
      const today = getLocalDateString();
      const stageIndex = stages.findIndex(s => s.id === stage.id);

      let minDate: string | undefined;
      for (let i = stageIndex - 1; i >= 0; i--) {
        const prev = stages[i];
        if ((prev.status === 'complete' || prev.status === 'locked') && prev.date) {
          minDate = prev.date.substring(0, 10);
          break;
        }
      }
      return { minDate, maxDate: today };
    },
    [stages]
  );

  const handleSave = async (stage: ProductionStage) => {
    const input = getStageInput(stage.id);
    if (!input.technicianId || input.initials.length < 2) return;

    const { minDate, maxDate } = getDateConstraints(stage);
    if (minDate && input.date < minDate) return;
    if (input.date > maxDate) return;

    setStageInputs(prev => ({
      ...prev,
      [stage.id]: { ...input, saving: true },
    }));

    try {
      await onSaveStage(stage.id, {
        technicianId: input.technicianId,
        date: input.date,
        initials: input.initials,
      });
      setStageInputs(prev => ({
        ...prev,
        [stage.id]: { ...input, saving: false, justSaved: true },
      }));
    } catch (err) {
      console.error('Failed to save production stage:', err);
      setStageInputs(prev => ({
        ...prev,
        [stage.id]: { ...input, saving: false },
      }));
    }
  };

  const isStageComplete = (stage: ProductionStage) =>
    stage.status === 'complete' || stage.status === 'locked';

  const isNextAvailableStage = (stage: ProductionStage) => {
    const stageIndex = stages.findIndex(s => s.id === stage.id);
    if (stageIndex === -1 || isStageComplete(stage)) return false;

    for (let i = 0; i < stageIndex; i++) {
      if (!isStageComplete(stages[i])) return false;
    }
    return true;
  };

  const isStageEditable = (stage: ProductionStage) => {
    if (canAdminOverride && isLocked) return true;
    if (!canRecordStages || isLocked) return false;
    if (stage.status === 'active') return true;
    return stage.status === 'pending' && isNextAvailableStage(stage);
  };

  const allComplete = stages.every(s => s.status === 'complete' || s.status === 'locked');

  return (
    <Box
      sx={{
        bgcolor: isLocked ? '#f0fff4' : '#fff',
        border: `1px solid ${isLocked ? '#27ae60' : '#e0e0e0'}`,
        borderRadius: '6px',
        p: 2,
      }}
    >
      <Typography
        sx={{
          fontSize: '13px',
          fontWeight: 600,
          color: '#666',
          textTransform: 'uppercase',
          mb: 1.5,
        }}
      >
        Production Progress{allComplete ? ' ✓ Complete' : ''}
      </Typography>

      {stages.map(stage => {
        const editable = isStageEditable(stage);
        const stageComplete = stage.status === 'complete' || stage.status === 'locked';
        const isAdminOverrideEdit = canAdminOverride && isLocked && stageComplete;
        const isComplete = stageComplete && !editable;
        const isPending = stage.status === 'pending';
        const showCheckmark = stageComplete;
        const input = getStageInput(stage.id);
        const { minDate, maxDate } = getDateConstraints(stage);
        const isDateInvalid =
          !input.date || input.date > maxDate || (!!minDate && input.date < minDate);

        const rowBg = stage.isFinalQc
          ? isComplete
            ? '#27ae60'
            : '#e8f5e9'
          : stage.isQcStage
            ? '#f8f9fa'
            : 'transparent';

        const textColor = stage.isFinalQc && isComplete ? '#fff' : undefined;

        const technicianDisplayName = getTechnicianDisplayName(
          stage.technicianId,
          stage.technicianName,
          technicians,
          inactiveTechnicianLookup
        );
        const editableTechnicianDisplayName = getTechnicianDisplayName(
          input.technicianId || stage.technicianId,
          input.technicianId && input.technicianId !== stage.technicianId
            ? null
            : stage.technicianName,
          technicians,
          inactiveTechnicianLookup
        );
        const dateDisplay = stage.date
          ? DateUtils.toDisplayProductionDate(stage.date)
          : input.date
            ? DateUtils.toDisplayDate(input.date)
            : '';
        const inputDateDisplay = input.date ? DateUtils.toDisplayDate(input.date) : dateDisplay;
        const initialsDisplay = input.initials || stage.initials || '';

        return (
          <Box
            key={stage.id}
            className="production-tracker-row"
            sx={{
              display: 'grid',
              // Pending rows also render a Save button (5th item), so they need the
              // 5-column template just like editable rows. The readonly complete state
              // has no Save button and uses the 4-column template.
              gridTemplateColumns:
                editable || isPending
                  ? { xs: '90px 1fr 105px 48px 72px', sm: '110px 1fr 125px 56px 72px' }
                  : { xs: '90px 1fr 105px 48px', sm: '110px 1fr 125px 56px' },
              gap: 1,
              alignItems: 'center',
              minWidth: 0,
              py: 1,
              px: stage.isQcStage || stage.isFinalQc ? 2 : 0,
              mx: stage.isQcStage || stage.isFinalQc ? -2 : 0,
              bgcolor: rowBg,
              borderBottom: stage.isFinalQc ? 'none' : '1px solid #f0f0f0',
              borderTop: stage.isFinalQc ? '2px solid #27ae60' : 'none',
              borderRadius: stage.isFinalQc ? '0 0 6px 6px' : 0,
              mt: stage.isFinalQc ? 1 : 0,
              mb: stage.isFinalQc ? -2 : 0,
            }}
          >
            {/* Stage Name */}
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                minWidth: 0,
              }}
            >
              {showCheckmark ? (
                <CheckCircleIcon
                  sx={{
                    fontSize: 20,
                    color: stage.isFinalQc && isComplete ? '#fff' : '#27ae60',
                    flexShrink: 0,
                  }}
                />
              ) : (
                <RadioButtonUncheckedIcon
                  sx={{
                    fontSize: 20,
                    color: isPending ? '#ccc' : '#888',
                    flexShrink: 0,
                  }}
                />
              )}
              <Typography
                sx={{
                  fontWeight: 500,
                  fontSize: '12px',
                  color: textColor,
                  overflow: 'hidden',
                  textOverflow: 'ellipsis',
                  whiteSpace: 'nowrap',
                  minWidth: 0,
                  flex: 1,
                }}
              >
                {stage.stageName}
              </Typography>
            </Box>

            {/* Complete: read-only values */}
            {isComplete && (
              <>
                <Typography
                  sx={{
                    fontSize: '12px',
                    color: textColor ?? '#27ae60',
                    fontWeight: 500,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    minWidth: 0,
                  }}
                  title={technicianDisplayName || undefined}
                >
                  {technicianDisplayName}
                </Typography>
                <Typography
                  sx={{
                    fontSize: '12px',
                    color: textColor ?? '#27ae60',
                    fontWeight: 500,
                  }}
                >
                  {dateDisplay}
                </Typography>
                <Typography
                  sx={{
                    fontSize: '12px',
                    color: textColor ?? '#27ae60',
                    fontWeight: 500,
                  }}
                >
                  {initialsDisplay}
                </Typography>
              </>
            )}

            {/* Editable inputs (active stage or admin override) */}
            {editable && (
              <>
                {/* Shown only in PDF export mode via injected CSS */}
                {!isAdminOverrideEdit && (
                  <Box
                    className="pdf-pending-placeholder"
                    sx={{
                      display: 'none',
                      gridColumn: '2 / -1',
                      fontSize: '12px',
                      color: '#999',
                      fontStyle: 'italic',
                    }}
                  >
                    {stage.status === 'active' ? '— In Progress' : '— Pending'}
                  </Box>
                )}
                {/* Admin is editing a stage that already has saved production data.
                    The editable controls above are hidden in the PDF export, so render
                    the saved technician/date/initials as read-only text that is hidden
                    on screen but revealed in the PDF (see injected `.pdf-complete-values`
                    styles in work-order-traveler-page). */}
                {isAdminOverrideEdit && (
                  <>
                    <Typography
                      className="pdf-complete-values"
                      sx={{ display: 'none', fontSize: '12px', color: '#27ae60', fontWeight: 500 }}
                      title={editableTechnicianDisplayName || undefined}
                    >
                      {editableTechnicianDisplayName}
                    </Typography>
                    <Typography
                      className="pdf-complete-values"
                      sx={{ display: 'none', fontSize: '12px', color: '#27ae60', fontWeight: 500 }}
                    >
                      {inputDateDisplay}
                    </Typography>
                    <Typography
                      className="pdf-complete-values"
                      sx={{ display: 'none', fontSize: '12px', color: '#27ae60', fontWeight: 500 }}
                    >
                      {initialsDisplay}
                    </Typography>
                  </>
                )}
                <FormControl size="small" fullWidth>
                  <Select
                    value={input.technicianId}
                    onChange={e => updateStageInput(stage.id, 'technicianId', e.target.value)}
                    displayEmpty
                    sx={{
                      fontSize: '12px',
                      '& .MuiSelect-select': {
                        overflow: 'hidden',
                        textOverflow: 'ellipsis',
                        whiteSpace: 'nowrap',
                      },
                    }}
                  >
                    <MenuItem value="" disabled>
                      Select Technician...
                    </MenuItem>
                    {isAdminOverrideEdit &&
                      stage.technicianId &&
                      !technicians.some(t => String(t.id) === String(stage.technicianId)) && (
                        <MenuItem
                          value={String(stage.technicianId)}
                          disabled
                          sx={{
                            fontSize: '12px',
                            fontStyle: 'italic',
                            opacity: 0.7,
                          }}
                        >
                          {stage.technicianName ?? 'Inactive Technician'} (Inactive)
                        </MenuItem>
                      )}
                    {technicians.map(tech => (
                      <MenuItem
                        key={tech.id}
                        value={String(tech.id)}
                        sx={{
                          fontSize: '12px',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                          display: 'block',
                          maxWidth: '300px',
                        }}
                      >
                        {tech.firstName} {tech.lastName}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <TextField
                  type="date"
                  size="small"
                  value={input.date}
                  onChange={e => updateStageInput(stage.id, 'date', e.target.value)}
                  inputProps={{
                    min: minDate,
                    max: maxDate,
                  }}
                  InputProps={{ sx: { fontSize: '12px' } }}
                />
                <TextField
                  size="small"
                  placeholder="Init"
                  value={input.initials}
                  onChange={e => updateStageInput(stage.id, 'initials', e.target.value)}
                  inputProps={{ maxLength: 3 }}
                  InputProps={{
                    sx: { fontSize: '12px', textTransform: 'uppercase' },
                  }}
                />
                <Button
                  variant="contained"
                  size="small"
                  disabled={
                    input.saving ||
                    input.justSaved ||
                    !input.technicianId ||
                    input.initials.length < 2 ||
                    isDateInvalid
                  }
                  onClick={() => handleSave(stage)}
                  sx={{
                    fontSize: '12px',
                    fontWeight: 600,
                    minHeight: 32,
                    minWidth: 0,
                    px: 1,
                    whiteSpace: 'nowrap',
                    bgcolor: input.justSaved ? '#27ae60' : undefined,
                    '&:hover': {
                      bgcolor: input.justSaved ? '#219a52' : undefined,
                    },
                  }}
                >
                  {input.justSaved ? '✓' : input.saving ? '...' : 'Save'}
                </Button>
              </>
            )}

            {/* Pending: disabled inputs */}
            {isPending && !isComplete && !editable && (
              <>
                {/* Shown only in PDF export mode via injected CSS */}
                <Box
                  className="pdf-pending-placeholder"
                  sx={{
                    display: 'none',
                    gridColumn: '2 / -1',
                    fontSize: '12px',
                    color: '#ccc',
                    fontStyle: 'italic',
                  }}
                >
                  — Pending
                </Box>
                <FormControl size="small" fullWidth>
                  <Select value="" disabled displayEmpty sx={{ fontSize: '12px' }}>
                    <MenuItem value="" disabled>
                      {stage.isQcStage ? 'Select QC...' : 'Select Technician...'}
                    </MenuItem>
                  </Select>
                </FormControl>
                <TextField
                  type="date"
                  size="small"
                  disabled
                  InputProps={{ sx: { fontSize: '12px' } }}
                />
                <TextField
                  size="small"
                  placeholder="Init"
                  disabled
                  InputProps={{ sx: { fontSize: '12px' } }}
                />
                <Button
                  variant="contained"
                  size="small"
                  disabled
                  sx={{
                    fontSize: '12px',
                    fontWeight: 600,
                    minHeight: 32,
                    minWidth: 0,
                    px: 1,
                    whiteSpace: 'nowrap',
                  }}
                >
                  Save
                </Button>
              </>
            )}
          </Box>
        );
      })}
    </Box>
  );
};

export default ProductionTracker;
